/** Single-pass tokenizer for the query-string syntax.
 *
 * No lexer modes: one linear scan producing a flat token stream plus tolerant
 * diagnostics. The parser turns this stream into a `FilterNode[]` tree.
 *
 * The identifier-vs-value distinction lives here: a "run" of characters that
 * matches `identifier(=~|=|:)rest` becomes an NSOP (namespace-operator) token,
 * optionally followed by a WORD for the captured rest; anything else stays a
 * WORD. A digit-first run (e.g. `08:00`) fails the identifier pattern and stays
 * a single WORD, which is what lets bare times/dates survive as values.
 */
import type { Diagnostic } from './types';

export type Sigil = '=' | '=~' | '=#' | ':';

export type Token =
  | { kind: 'WORD'; text: string; pos: number }
  | { kind: 'QUOTED'; text: string; pos: number }
  | { kind: 'NSOP'; ns: string; sigil: Sigil; all: boolean; pos: number }
  | { kind: 'RANGE'; pos: number }
  | { kind: 'AMP'; pos: number }
  | { kind: 'PIPE'; pos: number }
  | { kind: 'BANG'; pos: number }
  | { kind: 'LPAREN'; pos: number }
  | { kind: 'RPAREN'; pos: number };

/** Structural single-char punctuation that terminates a run. */
const PUNCT = new Set(['(', ')', '&', '|', '!', ',']);
/** Whitespace test. */
const isSpace = (c: string): boolean => c === ' ' || c === '\t' || c === '\n' || c === '\r';

/** identifier(=#|=~|=|:)(*?)rest — the run→NSOP test. `=#` is the match-by-id
 *  sigil (→ operator `exact`; value is a row id), listed before `=`/`=~` so the
 *  longer sigil wins. A `*` right after the sigil is the any/all modifier (all
 *  bookings in the order must match); a literal `*` in a value survives by being
 *  quoted (see serializer SPECIAL). */
const NSOP_RE = /^([A-Za-z_][A-Za-z0-9_]*)(=#|=~|=|:)(\*?)(.*)$/;

export function tokenize(text: string): { tokens: Token[]; diagnostics: Diagnostic[] } {
  const tokens: Token[] = [];
  const diagnostics: Diagnostic[] = [];
  let i = 0;
  const n = text.length;

  while (i < n) {
    const c = text[i]!;

    // Skip whitespace.
    if (isSpace(c)) {
      i += 1;
      continue;
    }

    // Structural punctuation (single char).
    if (c === '(') { tokens.push({ kind: 'LPAREN', pos: i }); i += 1; continue; }
    if (c === ')') { tokens.push({ kind: 'RPAREN', pos: i }); i += 1; continue; }
    if (c === '&') { tokens.push({ kind: 'AMP', pos: i }); i += 1; continue; }
    if (c === '|') { tokens.push({ kind: 'PIPE', pos: i }); i += 1; continue; }
    if (c === '!') { tokens.push({ kind: 'BANG', pos: i }); i += 1; continue; }
    // `,` is reserved (it ends a run, and the serializer quotes values holding one)
    // but means nothing yet: a bare one separates like whitespace. Without this
    // branch a hand-edited `?q=tag=a,b` looped forever pushing empty WORDs.
    if (c === ',') {
      diagnostics.push({ pos: i, message: 'Unquoted "," — quote a value that contains a comma' });
      i += 1;
      continue;
    }

    // Quoted string.
    if (c === '"') {
      const start = i;
      i += 1;
      let out = '';
      let terminated = false;
      while (i < n) {
        const q = text[i]!;
        if (q === '\\' && i + 1 < n) {
          const next = text[i + 1]!;
          if (next === '"' || next === '\\') {
            out += next;
            i += 2;
            continue;
          }
          // Unknown escape — keep the backslash literally.
          out += q;
          i += 1;
          continue;
        }
        if (q === '"') {
          i += 1;
          terminated = true;
          break;
        }
        out += q;
        i += 1;
      }
      if (!terminated) {
        diagnostics.push({ pos: start, message: 'Unterminated quoted string.' });
      }
      tokens.push({ kind: 'QUOTED', text: out, pos: start });
      continue;
    }

    // Range operator `..` (only when not part of a run — a leading `..` here).
    if (c === '.' && text[i + 1] === '.') {
      tokens.push({ kind: 'RANGE', pos: i });
      i += 2;
      continue;
    }

    // Otherwise: scan a run up to whitespace / structural punct / quote / `..`.
    // A lone `.` inside the run is fine (decimals/dates); a `..` stops the run
    // BEFORE it (so it becomes a separate RANGE token next iteration).
    const start = i;
    let run = '';
    while (i < n) {
      const r = text[i]!;
      if (isSpace(r) || PUNCT.has(r) || r === '"') break;
      if (r === '.' && text[i + 1] === '.') break; // stop before `..`
      run += r;
      i += 1;
    }

    const m = NSOP_RE.exec(run);
    if (m) {
      const ns = m[1]!;
      const sigil = m[2] as Sigil;
      const star = m[3]!; // '' or '*'
      const rest = m[4]!;
      tokens.push({ kind: 'NSOP', ns, sigil, all: star === '*', pos: start });
      if (rest.length > 0) {
        // Position points at where the rest begins within the source.
        tokens.push({ kind: 'WORD', text: rest, pos: start + ns.length + sigil.length + star.length });
      }
    } else {
      tokens.push({ kind: 'WORD', text: run, pos: start });
    }
  }

  return { tokens, diagnostics };
}
